import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import RecordPosition from "../RecordPosition/RecordPosition";
import { similarDesingHook } from "./useHook";

const SimilarDesignModal = ({ isOpen, onClose, designId }) => {
  const {
    addSimilarDesign,
    deleteSimilarDesign,
    getSimilarDesingData,
    getSimilarDesignExpectSelected,
    updateSimilarDesignPosition,
  } = similarDesingHook();

  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedForPosition, setSelectedForPosition] = useState(null);

  const [designData, setdesignData] = useState([]);
  const [selectedSimilarIds, setSelectedSimilarIds] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [getHouseDesign, setGetHouseDesign] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !designId) return;
    setIsLoading(true);
    Promise.all([
      getSimilarDesignExpectSelected(setGetHouseDesign, designId),
      getSimilarDesingData(setdesignData, designId),
    ]).finally(() => setIsLoading(false));
  }, [isOpen, designId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (!isDropdownOpen) {
      setSearchQuery("");
    }
  }, [isDropdownOpen]);

  const handleToggleSimilar = (id) => {
    if (selectedSimilarIds.includes(id)) {
      setSelectedSimilarIds((prev) => prev.filter((pid) => pid !== id));
    } else {
      setSelectedSimilarIds((prev) => [...prev, id]);
    }
  };

  const handleRemoveChip = (id) => {
    setSelectedSimilarIds((prev) => prev.filter((pid) => pid !== id));
  };

  const filteredDesigns = Array.isArray(getHouseDesign)
    ? getHouseDesign.filter((prod) =>
      (prod.title || "Unknown Product")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    : [];

  const handleSubmitSimilarDesign = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = { designId, similarIds: selectedSimilarIds };
      await addSimilarDesign(payload);
      Swal.fire({
        title: "Success",
        text: "Similar design added successfully",
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        onClose();
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSimilarDesign = async (id) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await deleteSimilarDesign(id);
      Swal.fire({
        title: "Deleted",
        text: "Similar design has been deleted.",
        icon: "success",
        confirmButtonText: "OK",
      });
      setIsLoading(true);
      await Promise.all([
        getSimilarDesignExpectSelected(setGetHouseDesign, designId),
        getSimilarDesingData(setdesignData, designId),
      ]);
    } finally {
      setDeletingId(null);
      setIsLoading(false);
    }
  };

  const handlePositionClick = (relation) => {
    const junction = relation.SimilarHouseDesigns;
    if (!junction) return;
    setSelectedForPosition({
      recordId: junction.id,
      currentPosition: junction.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, position) => {
    await updateSimilarDesignPosition(recordId, position);
    setIsLoading(true);
    getSimilarDesingData(setdesignData, designId).finally(() =>
      setIsLoading(false)
    );
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedForPosition(null);
  };

  const mainProduct =
    designData && designData.length > 0
      ? designData.find((p) => p.id === designId)
      : null;

  if (!isOpen) return null;

  const isActionLoading = isLoading || isSubmitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[600px] max-w-[90vw] relative">
        <h2 className="text-xl font-bold mb-4">Add/Delete Similar Designs</h2>

        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        )}

        {/* Linked Products Section */}
        <div className="mb-4">
          <h3 className="text-md font-bold">Similar Designs</h3>
          <ul className="mt-2">
            {Array.isArray(designData) && designData.length > 0 ? (
              designData.map((relation) => {
                const junctionId = relation.SimilarHouseDesigns?.id;
                const isDeleting = deletingId === junctionId;
                return (
                  <li
                    key={relation.id}
                    className="mb-2 flex justify-between items-center"
                  >
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handlePositionClick(relation)}
                        disabled={isActionLoading || isDeleting}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium min-w-[2rem] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {relation.SimilarHouseDesigns?.position ?? "—"}
                      </button>
                      <span>{relation.title || "Unknown Product"}</span>
                    </span>
                    <button
                      type="button"
                      className="text-red-500 ml-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[1.5rem] flex justify-center"
                      onClick={() =>
                        handleDeleteSimilarDesign(junctionId)
                      }
                      disabled={isActionLoading || isDeleting}
                      title={isDeleting ? "Deleting..." : "Delete"}
                    >
                      {isDeleting ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></span>
                      ) : (
                        "×"
                      )}
                    </button>
                  </li>
                );
              })
            ) : (
              <p>No similar designs available</p>
            )}
          </ul>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {selectedSimilarIds?.map((id) => {
            const product = getHouseDesign.find((p) => p.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700"
              >
                {product?.title || "Unknown"}
                <button
                  className="ml-2 text-blue-700 hover:text-blue-900"
                  onClick={() => handleRemoveChip(id)}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            className="w-full border border-gray-300 bg-white px-4 py-2 rounded-md text-left focus:outline-none"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {selectedSimilarIds.length
              ? "Add more designs..."
              : "Select similar designs..."}
          </button>

          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow max-h-64 overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search designs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {filteredDesigns.length > 0 ? (
                filteredDesigns.map((prod) => (
                  <div
                    key={prod.id}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center"
                    onClick={() => handleToggleSimilar(prod.id)}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedSimilarIds.includes(prod.id) ||
                        (Array.isArray(designData) &&
                          designData.some((item) => item.id === prod.id))
                      }
                      readOnly
                      className="mr-2"
                    />

                    <span>{prod.title || "Unknown Product"}</span>
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500 text-center">
                  No designs found
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            className="bg-gray-400 text-white py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isActionLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center min-w-[80px] justify-center"
            onClick={handleSubmitSimilarDesign}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>

      {isPositionModalOpen && selectedForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedForPosition.currentPosition}
          recordId={selectedForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Similar House Design"
        />
      )}
    </div>
  );
};

export default SimilarDesignModal;
